/**
 * Расчёты по уровням внутри свечей: кластеры, перекос, крупные объёмы.
 *
 * Профиль отвечает «где торговали за отрезок», эти — «что происходило
 * внутри каждой свечи». Строки свечи — тот же профиль, только из одного
 * бара, поэтому собираются той же функцией: два способа сложить уровни
 * однажды разошлись бы в копейку, и кластер не совпал бы с профилем.
 */

import type { OrderFlowVenue } from '@specter/types';
import type { FlowBarData } from './data.js';
import { buildProfile, volumeOf, type FlowProfile, type ProfileRow } from './profile.js';

/** Кластер свечи: её строки и главный уровень внутри неё. */
export function clusterOf(
  bar: FlowBarData,
  step: number,
  venues: ReadonlySet<OrderFlowVenue>,
  group: number,
): FlowProfile {
  // Зона стоимости внутри одной свечи никому не нужна — не считаем.
  return buildProfile([bar], step, venues, group, 0);
}

export type ImbalanceSide = 'buy' | 'sell';

export interface Imbalance {
  readonly row: ProfileRow;
  readonly side: ImbalanceSide;
  /** Во сколько раз одна сторона больше другой; `Infinity` — другой нет вовсе. */
  readonly ratio: number;
}

/**
 * Перекос по диагонали.
 *
 * Покупатель, пришедший по рынку, забирает заявки продавцов на уровне
 * выше тех, у которых забирает продавец. Поэтому сравнивают не покупки и
 * продажи одной цены, а покупки уровня с продажами уровня под ним — и
 * продажи уровня с покупками уровня над ним. Так считают кластерные
 * терминалы, и так перекос означает то, что должен: одна сторона
 * съедала стакан, а другая почти не отвечала.
 *
 * `minVolume` — нижняя граница в долларах: перекос «100 против 0» на
 * сотне долларов — шум, а не сигнал.
 */
export function imbalancesOf(
  rows: readonly ProfileRow[],
  ratio: number,
  minVolume: number,
): Imbalance[] {
  const byIndex = new Map(rows.map((row) => [row.index, row]));
  const out: Imbalance[] = [];
  if (rows.length === 0) return out;

  /*
   * Края свечи сравнивать не с чем.
   *
   * У верхнего уровня нет уровня выше: цена туда не ходила. Считай его
   * «перекосом без предела» — и перекос стоял бы почти на каждом максимуме
   * и минимуме, то есть не значил бы ничего. А пустой уровень внутри
   * свечи — другое дело: цену там прошли, не встретив встречных сделок.
   */
  const lowest = Math.min(...rows.map((row) => row.index));
  const highest = Math.max(...rows.map((row) => row.index));

  for (const row of rows) {
    if (row.index > lowest && row.buy > 0 && row.buy >= minVolume) {
      const against = byIndex.get(row.index - 1)?.sell ?? 0;
      const times = against > 0 ? row.buy / against : Infinity;
      if (times >= ratio) out.push({ row, side: 'buy', ratio: times });
    }
    if (row.index < highest && row.sell > 0 && row.sell >= minVolume) {
      const against = byIndex.get(row.index + 1)?.buy ?? 0;
      const times = against > 0 ? row.sell / against : Infinity;
      if (times >= ratio) out.push({ row, side: 'sell', ratio: times });
    }
  }
  return out;
}

export interface ImbalanceStack {
  readonly side: ImbalanceSide;
  readonly low: number;
  readonly high: number;
  /** Сколько уровней подряд. */
  readonly levels: number;
}

/**
 * Стопки перекоса: несколько уровней подряд с перекосом в одну сторону.
 *
 * Одиночный перекос — эпизод. Три и больше подряд — место, где одна
 * сторона давила уверенно, и к нему цена часто возвращается как к опоре
 * или сопротивлению.
 */
export function stacksOf(imbalances: readonly Imbalance[], minLevels: number): ImbalanceStack[] {
  const out: ImbalanceStack[] = [];
  for (const side of ['buy', 'sell'] as const) {
    const rows = imbalances
      .filter((imbalance) => imbalance.side === side)
      .map((imbalance) => imbalance.row)
      .sort((a, b) => a.index - b.index);

    let start = 0;
    for (let i = 1; i <= rows.length; i += 1) {
      const broken = i === rows.length || rows[i]!.index !== rows[i - 1]!.index + 1;
      if (!broken) continue;
      const run = i - start;
      if (run >= minLevels) {
        out.push({ side, low: rows[start]!.low, high: rows[i - 1]!.high, levels: run });
      }
      start = i;
    }
  }
  return out;
}

/**
 * Обычный объём уровня — медиана по всем непустым уровням свечей отрезка.
 *
 * Медиана, а не среднее: один взрыв объёма сделал бы «обычным» всё,
 * что меньше его половины, и крупные уровни перестали бы находиться
 * ровно тогда, когда их больше всего.
 */
export function typicalLevelVolume(clusters: readonly FlowProfile[]): number {
  const volumes: number[] = [];
  for (const cluster of clusters) for (const row of cluster.rows) volumes.push(volumeOf(row));
  if (volumes.length === 0) return 0;
  volumes.sort((a, b) => a - b);
  return volumes[Math.floor(volumes.length / 2)]!;
}

/** Насколько уровень крупнее обычного; округлено так, как прочтёт человек. */
export function relativeVolume(row: ProfileRow, typical: number): number {
  return typical > 0 ? Math.round((volumeOf(row) / typical) * 10) / 10 : 0;
}

/** Ступень крупности словами — те же пороги, что в карточке уровня. */
export function heavinessOf(relative: number): 'extreme' | 'large' | 'raised' | null {
  if (relative >= 5) return 'extreme';
  if (relative >= 3) return 'large';
  if (relative >= 2) return 'raised';
  return null;
}

export const HEAVINESS_TITLE = {
  extreme: 'экстремальный',
  large: 'крупный',
  raised: 'повышенный',
} as const;
